import React from 'react';
import { formatMoney, formatPercent, signClass } from '../../hooks/useResultsAnalysis';

const SOURCE_LABELS = [
  ['renovationValue', 'Value created by renovation'],
  ['marketAppreciation', 'Market appreciation (assumed)'],
  ['loanPaydown', 'Loan paid down'],
  ['cashFlow', 'Rent after all costs'],
  ['costs', 'Closing and selling costs'],
];

function breakEvenSentence(name, breakEven, entered) {
  if (breakEven == null) return null;
  if (breakEven <= -50) return `${name} ends ahead of flipping at any plausible change in prices.`;
  const rate = `${formatPercent(breakEven)}% a year`;
  if (breakEven <= 0) {
    return `${name} ends ahead of flipping even if prices do not rise (it would take a change of ${rate} to end level).`;
  }
  const verdict = entered > breakEven ? 'so on these inputs holding ends ahead' : 'so on these inputs flipping ends ahead';
  return `${name} ends ahead of flipping only if the property gains more than ${rate}. You entered ${formatPercent(entered)}%, ${verdict}.`;
}

export default function SameHorizonPanel({ analysis }) {
  const { sameHorizon, holdingPeriod, annualAppreciationPercent, handleSensitivityChange, monthlyRent, annualStrRevenue, nightlyRateEstimated } = analysis;
  const { flip, ltr, str, leaveInvested, years, reinvestmentRate, startingCash } = sameHorizon;
  const options = [
    { key: 'flip', label: 'Flip, then reinvest', data: flip, tone: 'border-terminal-green', show: true },
    { key: 'ltr', label: 'Hold as long-term rental', data: ltr, tone: 'border-terminal-cyan', show: monthlyRent > 0 },
    { key: 'str', label: 'Hold as short-term rental', data: str, tone: 'border-terminal-amber', show: annualStrRevenue > 0 && !nightlyRateEstimated },
    { key: 'invested', label: 'Skip the property, keep the cash invested', data: leaveInvested, tone: 'border-surface-border', show: true },
  ].filter((option) => option.show);
  const best = options.reduce((top, option) => (option.data.endingWealth > top.data.endingWealth ? option : top));
  const propertyOptions = options.filter((option) => option.key !== 'invested');
  const rentals = propertyOptions.filter((option) => option.key !== 'flip');
  // Flag a profit that rests on the appreciation assumption.
  const restsOnAppreciation = (sources) => {
    const gains = Object.values(sources).filter((value) => value > 0).reduce((a, b) => a + b, 0);
    return gains > 0 && sources.marketAppreciation / gains > 0.5;
  };

  return (
    <div className="mb-8">
      <h2 className="text-xl font-semibold text-txt-primary mb-2">The Same {years} Years</h2>
      <p className="text-sm text-txt-secondary mb-4">
        A flip gives your cash back after {holdingPeriod} months, while a rental ties it up for {years} years, so their
        yearly returns aren't a fair comparison. Here each option starts with the same {formatMoney(startingCash)}, and
        any cash outside the property earns the rate below. The figures are what you'd have after {years} years.
      </p>

      <label className="flex flex-wrap items-center gap-3 mb-4 text-sm text-txt-secondary" htmlFor="reinvestmentRate">
        Return on cash outside this property
        <span className="flex items-center gap-1">
          <input
            id="reinvestmentRate"
            type="number"
            inputMode="decimal"
            min="0"
            max="30"
            step="0.5"
            className="terminal-input w-24"
            value={reinvestmentRate}
            onChange={(e) => handleSensitivityChange('reinvestmentRate', e.target.value === '' ? 0 : parseFloat(e.target.value))}
          />
          <span>% a year</span>
        </span>
      </label>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        {options.map((option) => (
          <div key={option.key} className={`bg-surface-primary p-4 rounded border-l-4 ${option.tone}`}>
            <h3 className="text-sm font-medium text-txt-secondary mb-1">{option.label}</h3>
            <p className="text-2xl font-bold font-mono text-txt-primary">{formatMoney(option.data.endingWealth)}</p>
            <p className={`text-sm font-mono ${signClass(option.data.gain)}`}>
              {option.data.gain >= 0 ? '+' : ''}{formatMoney(option.data.gain)} on {formatMoney(startingCash)}
            </p>
            {option.key === best.key && options.length > 1 && (
              <p className="text-xs text-terminal-green uppercase tracking-wider mt-2">Most at year {years}</p>
            )}
          </div>
        ))}
      </div>

      {best.key === 'invested' && (
        <p className="text-sm text-terminal-amber mb-4">
          On these inputs, leaving the cash invested at {formatPercent(reinvestmentRate)}% a year ends ahead of every way of doing this deal.
        </p>
      )}

      {rentals.length > 0 && (
        <div className="bg-surface-elevated border border-surface-border rounded p-4 mb-4 space-y-2">
          {rentals.map((option) => (
            <p key={option.key} className="text-sm text-txt-primary">
              {breakEvenSentence(option.key === 'ltr' ? 'A long-term rental' : 'A short-term rental', option.data.breakEvenAppreciation, annualAppreciationPercent)}
            </p>
          ))}
        </div>
      )}

      {rentals.length > 0 && (
        <div className="bg-surface-primary rounded p-4">
          <h3 className="text-sm font-medium text-txt-primary mb-3">Where each rental's {years}-year profit comes from</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-txt-muted">
                  <th scope="col" className="py-2 pr-4 font-medium">Source</th>
                  {rentals.map((option) => (
                    <th key={option.key} scope="col" className="py-2 px-2 font-medium text-right">{option.key === 'ltr' ? 'Long-term' : 'Short-term'}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-border font-mono">
                {SOURCE_LABELS.map(([key, label]) => (
                  <tr key={key}>
                    <td className="py-2 pr-4 font-sans text-txt-secondary">{label}</td>
                    {rentals.map((option) => (
                      <td key={option.key} className={`py-2 px-2 text-right ${signClass(option.data.profitSources[key])}`}>
                        {formatMoney(option.data.profitSources[key])}
                      </td>
                    ))}
                  </tr>
                ))}
                <tr>
                  <td className="py-2 pr-4 font-sans font-medium text-txt-primary">Profit after {years} years</td>
                  {rentals.map((option) => {
                    const total = Object.values(option.data.profitSources).reduce((a, b) => a + b, 0);
                    return <td key={option.key} className={`py-2 px-2 text-right font-bold ${signClass(total)}`}>{formatMoney(total)}</td>;
                  })}
                </tr>
              </tbody>
            </table>
          </div>
          {rentals.some((option) => restsOnAppreciation(option.data.profitSources)) && (
            <p className="text-sm text-terminal-amber mt-3">
              More than half of the gain comes from rising prices, at the {formatPercent(annualAppreciationPercent)}% a year
              you assumed. Try a lower figure to see how much the result depends on it.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
